<template>
  <div>
    <div class="row mt-4 my-4">
      <div class="col-12">
        <TemplateBuilderHeader title="Templates">
          <SwitchButton
            id="templateSwitchButton"
            :value="activeView"
            :buttons="switchButtons"
            :on-click="setActiveView"
          />
          <router-link
            v-if="accessSettings.canCreateTemplate"
            :to="{name: 'TemplateSelector', query: $route.query }"
          >
            <button
              id="createTemplate"
              class="btn btn-lg btn-primary ml-4 px-4 white"
            >
              Create Template
            </button>
          </router-link>
        </TemplateBuilderHeader>
        <ejs-toast
          ref="errorToastRefA"
          :position="{ X: 'Right', Y: 'Top' }"
          show-close-button="true"
        />
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <div v-if="activeViewType === SAVED_VIEW_TYPE || activeViewType === SAVED_VIEW_TYPE">
          <savedTemplateNavBar />
        </div>
        <div v-if="activeViewType === PREBUILT_VIEW_TYPE || activeViewType === PREBUILT_VIEW_TYPE">
          <prebuiltTemplateNavBar />
        </div>
        <div v-if="activeView === 'table'">
          <div v-if="activeViewType === SAVED_VIEW_TYPE">
            <TemplateCards
              :details="details"
              :edit="edit"
              :clone="clone"
              :remove="remove"
            />
          </div>
          <div v-if="activeViewType === PREBUILT_VIEW_TYPE">
            <PreBuiltTemplateCards
              :on-click-use="onClickUse"
              :edit="edit"
              :clone="clone"
              :prebuilt-clone="prebuiltClone"
              :remove="removePrebuilt"
            />
          </div>
        </div>
        <div v-if="activeView === 'list'">
          <div v-if="activeViewType === SAVED_VIEW_TYPE">
            <TemplateGrid
              :details="details"
              :edit="edit"
              :clone="clone"
              :remove="remove"
            />
          </div>
          <div v-if="activeViewType === PREBUILT_VIEW_TYPE">
            <PreBuiltTemplateGrid
              :details="details"
              :edit="edit"
              :clone="clone"
              :prebuilt-clone="prebuiltClone"
              :remove="removePrebuilt"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import TemplateBuilderHeader from '../components/TemplateBuilderHeader.vue'
import SwitchButton from '../components/SwitchButton.vue'
import TemplateCards from '../components/TemplateCards'
import TemplateGrid from '../components/TemplateGrid.vue'
import savedTemplateNavBar from '../components/savedTemplatesNavBar.vue'
import prebuiltTemplateNavBar from '../components/prebuiltTemplatesNavBar.vue'
import { PREBUILT_VIEW_TYPE, SAVED_VIEW_TYPE } from '../constants/general'
import { TemplateGrid as PreBuiltTemplateGrid, TemplateCards as PreBuiltTemplateCards } from '../components/PreBuilt'
import { iframeParentCommunication } from '@/mixins/iframeParentCommunication'

export default {
  components: {
    TemplateBuilderHeader,
    SwitchButton,
    TemplateCards,
    TemplateGrid,
    PreBuiltTemplateGrid,
    PreBuiltTemplateCards,
    savedTemplateNavBar,
    prebuiltTemplateNavBar
  },
  mixins: [iframeParentCommunication],
  data () {
    const encodedUrlQuery = this.$helpers.getTemplateBuilderQuery(this.$route, false)
    const unencodedUrlQuery = { ...encodedUrlQuery }
    unencodedUrlQuery.userEmail = atob(unencodedUrlQuery.userEmail)
    return {
      encodedUrlQuery,
      unencodedUrlQuery,
      PREBUILT_VIEW_TYPE,
      SAVED_VIEW_TYPE,
      switchButtons: [
        {
          value: 'table',
          icon: ['far', 'border-all']
        },
        {
          value: 'list',
          icon: ['far', 'list']
        }
      ]
    }
  },
  computed: {
    ...mapGetters({
      activeView: 'getActiveView',
      accountId: 'getAccountId',
      paging: 'getPaging',
      accessSettings: 'getAccessSettings',
      activeViewType: 'getActiveType',
      activeType: 'getActiveType',
      globalUserPermission: 'getGlobalUserPermission',
      getGlobalTemplates: 'getGlobalTemplates'
    }),
    loadingStatus () {
      return this.$store.getters.getLoadingStatusItem('templates')
    }
  },
  watch: {
    accountId () {
      this.init()
    }
  },
  mounted () {
    this.init()
  },
  methods: {
    ...mapActions([
      'setActiveView'
    ]),
    init () {
      if (this.accountId) {
        const userEmail = this.$route.query.userEmail ? atob(this.$route.query.userEmail) : null
        const order = this.paging.order
        const dataState = {
          action: {
            currentPage: this.paging?.page ?? 1
          },
          skip: 0,
          take: this.paging?.pageSize ?? 12,
          sorted: [
            {
              name: order.column,
              direction: order.direction
            }
          ]
        }
        if (userEmail) {
          this.$store.dispatch('setTemplates', { userEmail, dataState })
        }
      }
    },
    details (templateId) {
      this.$router.push({ name: 'TemplateDetailsSummary', params: { id: templateId }, query: { ...this.encodedUrlQuery, templateId } })
    },
    onClickUse (templateId) {
      // TODO: create action/link for "use" the pre-built template
      // console.log('[DEBUG]', templateId)
      this.$router.push({ name: 'TemplateDetailsSummary', params: { id: templateId }, query: { ...this.encodedUrlQuery, templateId } })
    },
    edit (templateId, editorTypeId) {
      if (editorTypeId === 1) {
        this.$router.push({ name: 'TemplateEditor', params: { id: templateId }, query: { ...this.encodedUrlQuery, isGlobal: this.activeType === 'prebuilt' } })
      }
      if (editorTypeId === 2) {
        this.$router.push({ name: 'TemplateCreatorDragAndDrop', params: { id: templateId }, query: { ...this.encodedUrlQuery, isGlobal: this.activeType === 'prebuilt' } })
      }
      if (editorTypeId === 3) {
        this.$router.push({ name: 'TemplateCreatorCustomCode', params: { id: templateId }, query: { ...this.encodedUrlQuery, isGlobal: this.activeType === 'prebuilt' } })
      }
    },
    clone (templateId, editorTypeId) {
      if (editorTypeId === 1) {
        const currentGlobalTemplate = this.getGlobalTemplates.result.filter(e => e.id === templateId)
        if (currentGlobalTemplate.length) {
          const response = this.$store.dispatch('prebuiltCloneTemplate', { ...this.unencodedUrlQuery, templateId })
          response.then(({ success, error }) => {
            if (success) {
              this.$router.push({ name: 'TemplateEditor', params: { id: success.templateId }, query: { ...this.encodedUrlQuery, isGlobal: this.activeType === 'prebuilt' } })
            } else if (error) {
              console.log(error)
            }
          })
        } else {
          const response = this.$store.dispatch('cloneTemplate', { ...this.unencodedUrlQuery, templateId })
          response.then(({ success, error }) => {
            if (success) {
              this.$router.push({ name: 'TemplateEditor', params: { id: success.templateId }, query: { ...this.encodedUrlQuery, isGlobal: this.activeType === 'prebuilt' } })
            } else if (error) {
              console.log(error)
            }
          })
        }
      }
      if (editorTypeId === 2) {
        const currentGlobalTemplate = this.getGlobalTemplates.result.filter(e => e.id === templateId)
        if (currentGlobalTemplate.length) {
          const response = this.$store.dispatch('prebuiltCloneTemplate', { ...this.unencodedUrlQuery, templateId })
          response.then(({ success, error }) => {
            if (success) {
              this.$router.push({ name: 'TemplateCreatorDragAndDrop', params: { id: success.templateId }, query: { ...this.encodedUrlQuery, isGlobal: this.activeType === 'prebuilt' } })
            } else if (error) {
              console.log(error)
            }
          })
        } else {
          const response = this.$store.dispatch('cloneTemplate', { ...this.unencodedUrlQuery, templateId })
          response.then(({ success, error }) => {
            if (success) {
              this.$router.push({ name: 'TemplateCreatorDragAndDrop', params: { id: success.templateId }, query: { ...this.encodedUrlQuery, isGlobal: this.activeType === 'prebuilt' } })
            } else if (error) {
              console.log(error)
            }
          })
        }
      }
      if (editorTypeId === 3) {
        const currentGlobalTemplate = this.getGlobalTemplates.result.filter(e => e.id === templateId)
        if (currentGlobalTemplate.length) {
          const response = this.$store.dispatch('prebuiltCloneTemplate', { ...this.unencodedUrlQuery, templateId })
          response.then(({ success, error }) => {
            if (success) {
              this.$router.push({ name: 'TemplateCreatorCustomCode', params: { id: success.templateId }, query: { ...this.encodedUrlQuery, isGlobal: this.activeType === 'prebuilt' } })
            } else if (error) {
              console.log(error)
            }
          })
        } else {
          const response = this.$store.dispatch('cloneTemplate', { ...this.unencodedUrlQuery, templateId })
          response.then(({ success, error }) => {
            if (success) {
              this.$router.push({ name: 'TemplateCreatorCustomCode', params: { id: success.templateId }, query: { ...this.encodedUrlQuery, isGlobal: this.activeType === 'prebuilt' } })
            } else if (error) {
              console.log(error)
            }
          })
        }
      }
    },
    prebuiltClone (templateId, editorTypeId) {
      this.$store.dispatch('getPrebuiltTemplate', { ...this.unencodedUrlQuery, templateId })
        .then(({ template: { name, description }, version: { htmlJson, styleJson, html }, tags }) => {
          const templateData = {
            name,
            description,
            htmlJson,
            styleJson,
            html,
            tags: tags.map(tag => tag.name),
            accountIds: [this.accountId],
            editorTypeId: editorTypeId,
            isGlobal: false,
            culture: 'en-US'
          }
          this.$store.dispatch('createTemplate', { ...this.unencodedUrlQuery, data: templateData })
            .then(({ success }) => {
              if (editorTypeId === 1) {
                this.$router.push({ name: 'TemplateEditor', params: { id: success.templateId }, query: this.$route.query })
              }
              if (editorTypeId === 2) {
                this.$router.push({ name: 'TemplateCreatorDragAndDrop', params: { id: success.templateId }, query: this.$route.query })
              }
              if (editorTypeId === 3) {
                this.$router.push({ name: 'TemplateCreatorCustomCode', params: { id: success.templateId }, query: this.$route.query })
              }
            })
            .catch(error => {
              console.log(error)
            })
        })
    },
    remove (templateId) {
      return new Promise((resolve, reject) => {
        const response = this.$store.dispatch('deleteTemplate', { ...this.unencodedUrlQuery, templateId })
        response.then(({ success, error }) => {
          if (success) {
            resolve({ success })
          } else if (error) {
            reject(error)
            console.log(error)
          }
        })
      })
    },
    removePrebuilt (templateId) {
      return new Promise((resolve, reject) => {
        const response = this.$store.dispatch('deletePrebuiltTemplate', { ...this.unencodedUrlQuery, templateId })
        response.then(({ success, error }) => {
          if (success) {
            resolve({ success })
          } else if (error) {
            reject(error)
            console.log(error)
          }
        })
      })
    }
  }
}
</script>

<style lang="scss" scoped>
  .e-btn-primary {
    border-radius: 2px;
    color: $white;
    background-color: $bocalipstick;
    border-color: $bocalipstick-dark;
  }
</style>
